import { createContext, useContext } from 'react';
import type { UserProfile } from '../types/user';

export type AuthModalMode = 'login' | 'signup';

/**
 * Minimal user shape used by the app.
 */
export type AuthUser = {
  uid: string;
  email?: string | null;
  emailVerified?: boolean;
  displayName?: string | null;
  photoURL?: string | null;
};

export type AuthContextValue = {
  /**
   * True when auth is configured (env vars present) and auth can be used.
   */
  isAuthConfigured: boolean;
  /**
   * True once we've checked auth state at least once (or determined auth is not configured).
   */
  isAuthReady: boolean;
  /**
   * Current authenticated user, if signed in.
   */
  user: AuthUser | null;

  /**
   * User profile from database (null if not loaded or not signed in)
   */
  userProfile: UserProfile | null;

  /**
   * True once user profile has been loaded (or determined not to exist)
   */
  isProfileReady: boolean;

  /**
   * True when user is logged in but email is not yet verified
   */
  emailVerificationRequired: boolean;

  /**
   * Modal controls.
   */
  authModalOpen: boolean;
  authModalMode: AuthModalMode;
  openAuthModal: (mode?: AuthModalMode) => void;
  closeAuthModal: () => void;
  setAuthModalMode: (mode: AuthModalMode) => void;

  /**
   * Actions.
   */
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider />');
  }
  return ctx;
};
